import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import type { LexicalEditorRefActions } from "@/components/Editor/LexicalEditor";
import LexicalEditorWrapper from "@/components/Editor/LexicalEditor";
import type { DecisionData } from "@/components/Editor/nodes/DecisionNode";
import type { SelfContractData } from "@/components/Editor/nodes/SelfContractNode";
import { cn } from "@/lib/utils";
import type { EditorProps } from "../types";

interface LexicalSelectionLike {
  getTextContent?: () => string;
  anchor?: { offset?: number };
  set?: (...args: unknown[]) => void;
}

interface LexicalEditorLike {
  getElement?: () => Element | null;
  update: (callback: () => void) => void;
  getEditorState: () => { _selection?: LexicalSelectionLike };
}

export interface EditorRefActions {
  getEditor: () => unknown;
  focus: () => void;
  scrollToCursor: () => void;
  insertText: (text: string, prefix?: string, suffix?: string) => void;
  removeText: (start: number, length: number) => void;
  setContent: (text: string) => void;
  getContent: () => string;
  getSelectedContent: () => string;
  getCursorPosition: () => number;
  setCursorPosition: (startPos: number, endPos?: number) => void;
  getCursorLineNumber: () => number;
  getLine: (lineNumber: number) => string;
  setLine: (lineNumber: number, text: string) => void;
  insertSelfContract: (initialData?: Partial<SelfContractData>) => void;
  insertDecision: (initialData?: Partial<DecisionData>) => void;
  insertChecklist: () => void;
  reset: () => void;
}

const Editor = forwardRef<EditorRefActions, EditorProps>(function Editor(props, ref) {
  const {
    className,
    initialContent,
    placeholder,
    onPaste: _onPaste,
    onContentChange: handleContentChangeCallback,
    isFocusMode,
    editorInstanceId,
    autoFocus = false,
  } = props;

  const lexicalRef = useRef<LexicalEditorRefActions>(null);

  // Auto-focus on mount if autoFocus is true
  useEffect(() => {
    if (autoFocus && lexicalRef.current) {
      // Small delay to ensure editor is fully initialized
      setTimeout(() => {
        lexicalRef.current?.focus();
      }, 50);
    }
  }, [autoFocus]);

  // 使用 ref 存储 handleContentChangeCallback，避免 useCallback 依赖
  const onContentChangeCallbackRef = useRef(handleContentChangeCallback);
  onContentChangeCallbackRef.current = handleContentChangeCallback;

  // 处理内容变化 - 不使用 useCallback，避免依赖项变化
  const handleContentChange = (content: string, _jsonContent: unknown) => {
    onContentChangeCallbackRef.current(content);
  };

  // 实现兼容的 EditorRefActions 接口 - 使用 useCallback 避免重新创建对象
  const getEditor = useCallback(() => lexicalRef.current?.getEditor() || null, []);
  const getLexicalEditor = useCallback(() => lexicalRef.current?.getEditor() as LexicalEditorLike | null, []);
  const focus = useCallback(() => lexicalRef.current?.focus(), []);
  const scrollToCursor = useCallback(() => {
    const editor = getLexicalEditor();
    if (editor) {
      editor.getElement?.()?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [getLexicalEditor]);
  const insertText = useCallback(
    (text: string, prefix = "", suffix = "") => {
      const editor = getLexicalEditor();
      if (!editor) return;

      editor.update(() => {
        const selection = editor.getEditorState()._selection;
        if (selection) {
          const fullText = prefix + text + suffix;
          const currentContent = lexicalRef.current?.getContent() || "";
          lexicalRef.current?.setContent(currentContent + fullText);
        }
      });
    },
    [getLexicalEditor],
  );
  const removeText = useCallback(
    (_start: number, _length: number) => {
      const editor = getLexicalEditor();
      if (!editor) return;

      try {
        console.warn("removeText not fully implemented for Lexical editor");
      } catch (e) {
        console.error("Failed to remove text:", e);
      }
    },
    [getLexicalEditor],
  );

  const setContent = useCallback((text: string) => {
    lexicalRef.current?.setContent(text);
  }, []);

  const getContent = useCallback(() => {
    return lexicalRef.current?.getContent() || "";
  }, []);

  const getSelectedContent = useCallback(() => {
    const editor = getLexicalEditor();
    if (!editor) return "";

    try {
      const selection = editor.getEditorState()._selection;
      if (selection && typeof selection.getTextContent === "function") {
        return selection.getTextContent();
      }
    } catch (_e) {
      // ignore
    }
    return "";
  }, [getLexicalEditor]);

  const getCursorPosition = useCallback(() => {
    const editor = getLexicalEditor();
    if (!editor) return 0;

    try {
      const selection = editor.getEditorState()._selection;
      if (selection && selection.anchor) {
        return selection.anchor.offset || 0;
      }
    } catch (_e) {
      // ignore
    }
    return 0;
  }, [getLexicalEditor]);

  const setCursorPosition = useCallback(
    (_startPos: number, _endPos?: number) => {
      const editor = getLexicalEditor();
      if (!editor) return;

      try {
        editor.update(() => {
          const selection = editor.getEditorState()._selection;
          if (selection && typeof selection.set === "function") {
            // Lexical 的选区设置逻辑
            // 暂时简化实现
          }
        });
      } catch (e) {
        console.error("Failed to set cursor position:", e);
      }
    },
    [getLexicalEditor],
  );

  const getCursorLineNumber = useCallback(() => {
    return 0;
  }, []);

  const getLine = useCallback((_lineNumber: number) => {
    return "";
  }, []);

  const setLine = useCallback((_lineNumber: number, _text: string) => {
    console.warn("setLine not fully implemented for Lexical editor");
  }, []);

  const insertSelfContract = useCallback((initialData?: Partial<SelfContractData>) => {
    lexicalRef.current?.insertSelfContract(initialData);
  }, []);

  const insertDecision = useCallback((initialData?: Partial<DecisionData>) => {
    lexicalRef.current?.insertDecision?.(initialData);
  }, []);

  const insertChecklist = useCallback(() => {
    lexicalRef.current?.insertChecklist?.();
  }, []);

  const resetEditor = useCallback(() => {
    lexicalRef.current?.reset();
  }, []);

  // 使用 useMemo 创建稳定的 editorActions 对象
  const editorActions: EditorRefActions = useMemo(
    () => ({
      getEditor,
      focus,
      scrollToCursor,
      insertText,
      removeText,
      setContent,
      getContent,
      getSelectedContent,
      getCursorPosition,
      setCursorPosition,
      getCursorLineNumber,
      getLine,
      setLine,
      insertSelfContract,
      insertDecision,
      insertChecklist,
      reset: resetEditor,
    }),
    [
      getEditor,
      focus,
      scrollToCursor,
      insertText,
      removeText,
      setContent,
      getContent,
      getSelectedContent,
      getCursorPosition,
      setCursorPosition,
      getCursorLineNumber,
      getLine,
      setLine,
      insertSelfContract,
      insertDecision,
      insertChecklist,
      resetEditor,
    ],
  );

  useImperativeHandle(ref, () => editorActions, [editorActions]);

  return (
    <div className={cn("flex flex-col justify-start items-start relative w-full bg-inherit", isFocusMode ? "flex-1" : "", className)}>
      <LexicalEditorWrapper
        ref={lexicalRef}
        initialContent={initialContent}
        placeholder={placeholder}
        onContentChange={handleContentChange}
        isFocusMode={isFocusMode}
        className="w-full"
        editorInstanceId={editorInstanceId}
      />
    </div>
  );
});

export default Editor;
